// Upstream providers: Discord, which can also sign you in, and GitHub, which is
// only ever connected from the profile page (#142).
//
// The sign-in leg is a full-page navigation to the API — it cannot be a fetch,
// because the browser has to follow the provider's redirects and come back. The
// link leg goes the other way (an authenticated POST that returns a URL), since
// a navigation can't carry the bearer token.

import { getBackendUrl } from './backend.js';

export const PROVIDER_DISCORD = 'discord';
export const PROVIDER_GITHUB = 'github';

// How the identities load went. Tracked explicitly because "no providers" and
// "the load failed" are different facts that both leave the provider list empty.
export const IDENTITY_STATUS_LOADING = 'loading';
export const IDENTITY_STATUS_LOADED = 'loaded';
export const IDENTITY_STATUS_ERROR = 'error';

// What the Connected accounts section should show.
export const CONNECTED_ACCOUNTS_ERROR = 'error';
export const CONNECTED_ACCOUNTS_EMPTY = 'empty';
export const CONNECTED_ACCOUNTS_LIST = 'list';

const DEFAULT_TENANT_SLUG = 'dicedriven';

const PROVIDER_LABELS = {
    [PROVIDER_DISCORD]: 'Discord',
    [PROVIDER_GITHUB]: 'GitHub',
};

// Bootstrap Icons class per provider. One with none gets a generic link icon
// rather than borrowing another provider's.
const PROVIDER_ICONS = {
    [PROVIDER_DISCORD]: 'bi-discord',
    [PROVIDER_GITHUB]: 'bi-github',
};

/**
 * The icon class for a provider.
 * @param {string} provider
 * @returns {string}
 */
export function providerIcon(provider) {
    return PROVIDER_ICONS[provider] || 'bi-link-45deg';
}

/**
 * Whether a linked account needs connecting again: the API says so when the
 * provider is one services act through (GitHub) and the link holds no working
 * authorization -- a revoked or expired refresh token.
 * @param {{needs_reconnect?: boolean}|undefined} identity
 * @returns {boolean}
 */
export function needsReconnect(identity) {
    return identity?.needs_reconnect === true;
}

// Why the sign-in was refused, keyed by the code the API redirects back with.
// The API owns the decision; the wording lives here.
const PROVIDER_ERROR_MESSAGES = {
    not_invited: 'New users must be invited through one of Dice Driven’s apps.',
    provider_no_email:
        'That account has no verified email address, so we can’t confirm it belongs to you. Add and verify an email with the provider, then try connecting again.',
    already_linked: 'That account is already connected to a different user.',
    provider_already_connected: 'You have already connected an account from this provider.',
    provider_failed: 'That sign-in could not be completed. Please try again.',
};

/**
 * Human name for a provider.
 * @param {string} provider
 * @returns {string}
 */
export function providerLabel(provider) {
    return PROVIDER_LABELS[provider] || provider;
}

/**
 * Message for a provider error code, with a safe fallback for a code this build
 * doesn't know about.
 * @param {string} code
 * @returns {string}
 */
export function providerErrorMessage(code) {
    return PROVIDER_ERROR_MESSAGES[code] || PROVIDER_ERROR_MESSAGES.provider_failed;
}

/**
 * The email a pending-email token was minted with — the address the provider
 * asserted. What it means depends on the token's mode: signing in, it prefills
 * an editable field; linking, it is the address that MUST be proven, and the
 * server enforces that. Returns '' for a token with no email or one that won't
 * parse.
 * @param {string} pendingToken
 * @returns {string}
 */
export function knownEmailFromPendingToken(pendingToken) {
    return pendingTokenClaim(pendingToken, 'known_email');
}

/**
 * Whether a pending-email token is finishing a link rather than a sign-in. In
 * link mode the email is fixed and the step connects an account instead of
 * starting a session.
 * @param {string} pendingToken
 * @returns {boolean}
 */
export function isLinkPendingToken(pendingToken) {
    return pendingTokenClaim(pendingToken, 'mode') === 'link';
}

function pendingTokenClaim(pendingToken, claim) {
    try {
        const payload = pendingToken.split('.')[1];
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return decoded[claim] || '';
    } catch {
        return '';
    }
}

/**
 * How to describe a connected account: what the provider calls the user, else
 * the address it reported, else nothing. Older links (or providers that report
 * neither) simply show no detail rather than a placeholder.
 * @param {{provider_email?: string, metadata?: {username?: string, global_name?: string}}} identity
 * @returns {string}
 */
export function identityDisplayName(identity) {
    const metadata = identity?.metadata || {};
    if (metadata.username) {
        // Discord retired the `#1234` discriminator in 2023 and reports "0" for
        // migrated accounts, so it is only ever present on a straggler.
        return metadata.discriminator
            ? `${metadata.username}#${metadata.discriminator}`
            : metadata.username;
    }
    return metadata.global_name || identity?.provider_email || '';
}

/**
 * Which of the three Connected accounts states to render. A failed load must
 * show its error, so it is decided before the provider list is consulted — an
 * empty list only means "this deployment offers no providers" once the load
 * actually succeeded.
 * @param {string} status one of the IDENTITY_STATUS_* values
 * @param {string[]} providers the providers this deployment offers
 * @returns {string} one of the CONNECTED_ACCOUNTS_* values
 */
export function connectedAccountsView(status, providers) {
    if (status === IDENTITY_STATUS_ERROR) return CONNECTED_ACCOUNTS_ERROR;
    return providers?.length ? CONNECTED_ACCOUNTS_LIST : CONNECTED_ACCOUNTS_EMPTY;
}

/**
 * The tenant a sign-in belongs to, read from a tenant-scoped path like
 * /oauth/{tenant}/authorize. Off such a path (the plain /login page) there is no
 * tenant in the URL, so fall back to the default.
 * @param {string} path
 * @returns {string}
 */
export function tenantSlugFromPath(path) {
    const match = path.match(/^\/oauth\/([^/]+)\//);
    return match ? match[1] : DEFAULT_TENANT_SLUG;
}

/**
 * Where the browser goes to begin a provider sign-in.
 * @param {string} provider
 * @param {string} tenantSlug
 * @returns {string}
 */
export function providerStartUrl(provider, tenantSlug) {
    return `${getBackendUrl()}/oauth/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(provider)}/start`;
}

// What a provider round trip came back to the completion page with.
export const COMPLETION_ERROR = 'error';
export const COMPLETION_PENDING = 'pending';
export const COMPLETION_LINKED = 'linked';
export const COMPLETION_TOKEN = 'token';
export const COMPLETION_FAILED = 'failed';

/**
 * Decide what the completion page should do with the query the API redirected
 * to it with. A refusal wins over everything; then an email still to prove;
 * then a link the API already made; then a sign-in token to redeem. Anything
 * else is a failure.
 *
 * A successful link says so with linked=1 rather than by carrying nothing:
 * "nothing to act on" is also what a broken round trip looks like, and reading
 * one as the other is how a working GitHub connect came to show an error.
 * @param {URLSearchParams} query
 * @param {string|null} [storedPendingToken] a pending token kept from an earlier visit
 * @returns {string} one of the COMPLETION_* values
 */
export function completionOutcome(query, storedPendingToken = null) {
    if (query.get('error')) return COMPLETION_ERROR;
    if (query.get('pending_token') || storedPendingToken) return COMPLETION_PENDING;
    if (query.get('linked') === '1') return COMPLETION_LINKED;
    if (query.get('token')) return COMPLETION_TOKEN;
    return COMPLETION_FAILED;
}
